import React from "react";
import { useV2GetChangeOrderByIdQuery } from "@generated";
import { useChangeOrderAccess } from "../hooks/useChangeOrderAccess";
import { causeLabel } from "../constants/causes";

interface Props {
  // From changeOrderChild(project): the change-order? flag, and the
  // parent-change-order linkage (null without change-order view).
  isChangeOrder: boolean;
  parentChangeOrderId: string | null;
}

interface IncludedProject {
  id?: string;
  type?: string;
  attributes?: { "project-name"?: string };
}

const bannerStyle: React.CSSProperties = {
  margin: "0 16px 8px",
  padding: "6px 12px",
  fontSize: 13,
};

/*
 * Shown on a change order's child project. The project chrome query only
 * includes `parent-change-order` (the nested parent-project include isn't in
 * the generated union), so the banner fetches the change order itself.
 * Users without `view` on change orders get no linkage (the API hides it),
 * so they see a plain notice from the project's change-order? flag; a failed
 * fetch falls back to the same notice.
 *
 * Links are plain anchors: the classic (react-nav off) header mounts this
 * outside any router.
 */
const ChangeOrderBanner = ({ isChangeOrder, parentChangeOrderId }: Props) => {
  const { canView } = useChangeOrderAccess();
  const { data, isError } = useV2GetChangeOrderByIdQuery(
    { id: parentChangeOrderId ?? "", include: ["parent-project"] },
    { skip: !parentChangeOrderId }
  );

  if (!isChangeOrder && !parentChangeOrderId) return null;

  if (!parentChangeOrderId || isError) {
    return (
      <div className="alert alert-secondary" role="status" style={bannerStyle}>
        This project is a change request
      </div>
    );
  }

  const changeOrder = data?.data;
  if (!changeOrder) return null;

  const label = `CR-${changeOrder.attributes?.number ?? 0}`;
  const parentId = changeOrder.relationships?.["parent-project"]?.data?.id;
  const parent = (data?.included as IncludedProject[] | undefined)?.find(
    (i) => i.type === "projects" && String(i.id) === String(parentId)
  );
  const parentName = parent?.attributes?.["project-name"] ?? "parent project";
  const links = canView && Boolean(parentId);

  return (
    <div className="alert alert-secondary" role="status" style={bannerStyle}>
      Change request{" "}
      <strong>
        {links ? (
          <a
            href={`/projects/${parentId}/change_orders/${changeOrder.id}/edit`}
          >
            {label}
          </a>
        ) : (
          label
        )}
      </strong>{" "}
      of{" "}
      <strong>
        {links ? (
          <a href={`/projects/${parentId}/project_editor`}>{parentName}</a>
        ) : (
          parentName
        )}
      </strong>{" "}
      · {causeLabel(changeOrder.attributes?.cause)}
    </div>
  );
};

export default ChangeOrderBanner;
